import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { GraduationCap, Rocket, Save, RotateCcw } from "lucide-react";
import { resetTextsCache } from "@/components/chat/ToolIntro";

function TextCard({ persona, k, label, value, onSaved }) {
  const [title, setTitle] = useState(value.title);
  const [body, setBody] = useState(value.body);
  useEffect(() => { setTitle(value.title); setBody(value.body); }, [value.title, value.body]);
  const save = async () => {
    try { await api.put("/admin/texts", { persona, key: k, title, body }); toast.success(`${label} saved`); resetTextsCache(); onSaved(); }
    catch { toast.error("Save failed"); }
  };
  const reset = async () => {
    try { await api.delete(`/admin/texts/${persona}/${k}`); toast.success("Default text restored"); resetTextsCache(); onSaved(); }
    catch { toast.error("Reset failed"); }
  };
  return (
    <Card className="p-5 rounded-2xl border-border space-y-3" data-testid={`text-card-${k}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-orange">{label}</span>
        {value.custom && <span className="text-[10px] font-semibold bg-navy/5 text-navy px-2 py-0.5 rounded-full">Customised</span>}
      </div>
      <Input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} data-testid={`text-title-${k}`} className="font-semibold" />
      <Textarea value={body} maxLength={3000} rows={6} onChange={(e) => setBody(e.target.value)} data-testid={`text-body-${k}`} className="text-sm" />
      <p className="text-[11px] text-muted-foreground">Formatting: **bold**, lists with “- ”, blank line = new paragraph.</p>
      <div className="flex justify-end gap-2">
        {value.custom && <Button variant="ghost" size="sm" onClick={reset} data-testid={`text-reset-${k}`} className="rounded-full"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Default</Button>}
        <Button size="sm" onClick={save} disabled={!title.trim() || !body.trim()} data-testid={`text-save-${k}`} className="rounded-full bg-orange hover:bg-orange-dark text-white"><Save className="h-3.5 w-3.5 mr-1" /> Save</Button>
      </div>
    </Card>
  );
}

export function TextsTab() {
  const [data, setData] = useState(null);
  const [persona, setPersona] = useState("undergraduate");
  const load = useCallback(() => api.get("/admin/texts").then((r) => setData(r.data)).catch(() => toast.error("Could not load texts")), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const btn = (p, Icon, label) => (
    <button onClick={() => setPersona(p)} data-testid={`texts-persona-${p}`}
      className={`inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold transition-colors ${persona === p ? "bg-navy text-white" : "bg-white border border-border text-navy"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">Explanatory texts shown on the dashboard (“What is Elite?”) and at the top of each tool, per student level.</p>
      <div className="flex gap-2">{btn("undergraduate", GraduationCap, "Undergraduate")}{btn("postgraduate", Rocket, "Postgraduate")}</div>
      <div className="grid lg:grid-cols-2 gap-5">
        {data.keys.map((k) => (
          <TextCard key={`${persona}-${k}`} persona={persona} k={k} label={data.labels[k]} value={data.texts[persona][k]} onSaved={load} />
        ))}
      </div>
    </div>
  );
}
